import { useState, useRef, useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/lib/toast";
import { IconArrowLeft } from "@tabler/icons-react";
import { Button, ErrorBlock } from "@/components/Ui";
import { AnimatedEmoji } from "@/components/AnimatedEmoji";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

type Mode = "signin" | "signup";

function getCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
	return match ? decodeURIComponent(match[2]) : null;
}

function FloatingField({
	id,
	label,
	type = "text",
	value,
	onChange,
	required,
	autoComplete,
	showToggle,
	autoFocus,
}: {
	id: string;
	label: string;
	type?: string;
	value: string;
	onChange: (v: string) => void;
	required?: boolean;
	autoComplete?: string;
	showToggle?: boolean;
	autoFocus?: boolean;
}) {
	const [focused, setFocused] = useState(false);
	const [hidden, setHidden] = useState(true);
	const filled = value.length > 0;
	const active = focused || filled;
	const inputType = showToggle ? (hidden ? "password" : "text") : type;

	return (
		<div style={{ position: "relative", marginBottom: "1.25rem" }}>
			<input
				id={id}
				type={inputType}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				required={required}
				autoComplete={autoComplete}
				autoFocus={autoFocus}
				placeholder=" "
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				style={{
					width: "100%",
					padding: showToggle ? "1.1rem 2.6rem 0.55rem 0.85rem" : "1.1rem 0.85rem 0.55rem",
					borderRadius: "var(--radius-md)",
					border: `1px solid ${focused ? "var(--color-primary)" : "var(--color-border)"}`,
					background: "var(--color-input-bg)",
					color: "var(--color-text)",
					fontSize: "0.95rem",
					outline: "none",
					transition: "border-color 200ms ease",
				}}
			/>
			<label
				htmlFor={id}
				style={{
					position: "absolute",
					left: "0.85rem",
					top: active ? "0.35rem" : "0.85rem",
					fontSize: active ? "0.68rem" : "0.95rem",
					fontWeight: active ? 600 : 400,
					color: active ? "var(--color-primary)" : "var(--color-muted)",
					pointerEvents: "none",
					transition: "all 200ms ease",
					transformOrigin: "left top",
				}}
			>
				{label}
			</label>
			{showToggle && (
				<button
					type="button"
					tabIndex={-1}
					onClick={() => setHidden((v) => !v)}
					style={{
						position: "absolute",
						right: "0.6rem",
						top: "50%",
						transform: "translateY(-50%)",
						background: "none",
						border: "none",
						cursor: "pointer",
						color: "var(--color-muted)",
						padding: "0.3rem",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
					aria-label={hidden ? "Tampilkan password" : "Sembunyikan password"}
				>
					{hidden ? (
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
							<circle cx="12" cy="12" r="3" />
						</svg>
					) : (
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
							<path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
							<path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
							<line x1="1" y1="1" x2="23" y2="23" />
						</svg>
					)}
				</button>
			)}
		</div>
	);
}

export default function AuthPage({ mode = "signin" }: { mode?: Mode }) {
	const { isLoaded, isSignedIn } = useAuth();
	const navigate = useNavigate();
	const turnstileRef = useRef<TurnstileInstance | null>(null);
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	// Read referral code from cookie on mount and store in localStorage
	useEffect(() => {
		const refCode = getCookie("yayy_ref");
		if (refCode) {
			localStorage.setItem("yayy_ref", refCode);
		}
	}, []);

	if (isLoaded && isSignedIn) {
		return <Navigate to="/dashboard" replace />;
	}

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		if (!turnstileToken) {
			toast.error("Silakan selesaikan verifikasi captcha");
			return;
		}
		setSubmitting(true);
		try {
		if (mode === "signin") {
			const res = await authClient.signIn.email({ email, password, fetchOptions: {
				headers: { "x-captcha-response": turnstileToken },
			} });
			if (res.error) {
				setError(res.error.message ?? "Gagal masuk");
				resetCaptcha();
				return;
			}
			const role = res.data?.user?.role;
			navigate(role === "admin" ? "/manage" : "/dashboard", { replace: true });
			} else {
				const res = await authClient.signUp.email({ name, email, password, fetchOptions: {
					headers: { "x-captcha-response": turnstileToken },
				} });
				if (res.error) {
					setError(res.error.message ?? "Gagal daftar");
					resetCaptcha();
					return;
				}
				// Process referral from cookie if exists
				const refCode = localStorage.getItem("yayy_ref");
				if (refCode) {
					try {
						const token = await authClient.getSession();
						const sessionToken = (token.data as any)?.session?.token;
						if (sessionToken) {
							await fetch("/api/referral/process", {
								method: "POST",
								headers: {
									"Content-Type": "application/json",
									Authorization: `Bearer ${sessionToken}`,
								},
								body: JSON.stringify({ refCode }),
							});
						}
					} catch {
						// best-effort, don't block signup
					}
					localStorage.removeItem("yayy_ref");
				}
				navigate(`/verify-otp?email=${encodeURIComponent(email)}`, { replace: true });
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Terjadi kesalahan");
		} finally {
			setSubmitting(false);
		}
	};

	function resetCaptcha() {
		turnstileRef.current?.reset();
		setTurnstileToken(null);
	}

	return (
		<div
			style={{
				flex: 1,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<div style={{ width: "100%", maxWidth: 360 }}>
				<div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
					<AnimatedEmoji name={mode === "signin" ? "gembok" : "tiket"} size={56} style={{ marginInline: "auto" }} />
					<h1
						style={{
							fontSize: "1.5rem",
							fontWeight: 800,
							margin: "0.75rem 0 0.35rem",
						}}
					>
						{mode === "signin" ? "Masuk" : "Daftar"}
					</h1>
					<p
						style={{
							fontSize: "0.85rem",
							color: "var(--color-muted)",
							margin: 0,
						}}
					>
						{mode === "signin"
							? "Masuk untuk lanjut ke kuis pasangan."
							: "Buat akun baru untuk mulai bermain."}
					</p>
				</div>
				<form onSubmit={onSubmit}>
					{mode === "signup" && (
						<FloatingField
							id="name"
							label="Nama"
							value={name}
							onChange={setName}
							required
							autoComplete="name"
							autoFocus
						/>
					)}
					<FloatingField
						id="email"
						label="Email"
						type="email"
						value={email}
						onChange={setEmail}
						required
						autoComplete="email"
						autoFocus={mode === "signin"}
					/>
					<FloatingField
						id="password"
						label="Password"
						type="password"
						value={password}
						onChange={setPassword}
						required
						autoComplete={mode === "signin" ? "current-password" : "new-password"}
						showToggle
					/>
					<div style={{ marginBottom: "1.25rem", display: "flex", justifyContent: "center" }}>
						{TURNSTILE_SITE_KEY ? (
							<Turnstile
								ref={turnstileRef}
								siteKey={TURNSTILE_SITE_KEY}
								options={{
									theme: "auto",
								}}
								onSuccess={(token) => setTurnstileToken(token)}
								onExpire={() => setTurnstileToken(null)}
							/>
						) : (
							<ErrorBlock message="Captcha tidak dikonfigurasi. Hubungi admin." />
						)}
					</div>
					{error && (
						<div style={{ marginBottom: "1rem" }}>
							<ErrorBlock message={error} />
						</div>
					)}
					<Button type="submit" disabled={submitting || !turnstileToken} variant="primary" style={{ width: "100%" }}>
						{submitting
							? "..."
							: mode === "signin"
								? "Masuk"
								: "Daftar"}
					</Button>
				</form>
				<p
					style={{
						fontSize: "0.85rem",
						marginTop: "1.25rem",
						textAlign: "center",
						color: "var(--color-muted)",
					}}
				>
					{mode === "signin" ? (
						<>
							Belum punya akun?{" "}
							<Link to="/register" style={{ color: "var(--color-primary)" }}>
								Daftar
							</Link>
						</>
					) : (
						<>
							Sudah punya akun?{" "}
							<Link to="/login" style={{ color: "var(--color-primary)" }}>
								Masuk
							</Link>
						</>
					)}
				</p>
				<p
					style={{
						fontSize: "0.82rem",
						marginTop: "1.25rem",
						textAlign: "center",
					}}
				>
					<Link
						to="/"
						style={{ color: "var(--color-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}
					>
						<IconArrowLeft size={16} /> Kembali ke beranda
					</Link>
				</p>
			</div>
		</div>
	);
}
